/*
 * Opted out of the React Compiler, and it has to be every form in the panel
 * rather than the ones that happen to break.
 *
 * `register()` returns a fresh `ref` callback on every render, and react-hook-form
 * depends on it: React detaches the old one and attaches the new, which is when
 * the library writes a value back into an uncontrolled input. The compiler
 * memoises the spread, the callback identity stops changing, React stops
 * calling it — and `reset(values)` silently leaves the DOM as the reader typed
 * it. Measured: discard restored nothing, and the next edit no longer raised
 * the unsaved-changes guard.
 *
 * The compiler already declines several of these on its own — that is the
 * `react-hooks/incompatible-library` warning — but which ones it declines is
 * not something to depend on.
 */
'use no memo'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { SESSION_QUERY_KEY } from '@/shared/config'
import { Alert, AlertDescription, Button, FloatingField } from '@/shared/ui'

import type { TokenFailure } from '../api/token-api'
import { signIn } from '../model/session-lifecycle'

/**
 * `credentials` stays deliberately vague — telling a wrong login apart from a
 * wrong password would confirm whether an account exists. The other three say
 * what they are, because none of them leaks that.
 */
const MESSAGE_KEYS: Record<TokenFailure, string> = {
  credentials: 'signIn.rejected',
  blocked: 'signIn.blocked',
  rate_limited: 'signIn.rateLimited',
  unavailable: 'signIn.unavailable',
}

/** The schema is the only source of validation — no checks inside the component. */
const signInSchema = z.object({
  username: z.string().min(1, { message: 'common:validation.required' }),
  password: z.string().min(1, { message: 'common:validation.required' }),
})

type SignInValues = z.infer<typeof signInSchema>

export function SignInForm({ returnTo }: { returnTo: string }) {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { username: '', password: '' },
    // Validating on blur paints a field red the moment focus leaves it, so
    // tabbing through the form is enough to light it up before anything has
    // been typed. Complain on submit, then correct live as the user fixes it.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onSubmit = handleSubmit(async (values) => {
    const result = await signIn(values.username, values.password)

    if (!result.ok) {
      // `root` because the failure belongs to neither field.
      setError('root', { message: t(MESSAGE_KEYS[result.reason]) })
      return
    }

    // The guard cached `null` before the form was shown. Left in place, the
    // navigation below would read it and bounce straight back here.
    queryClient.removeQueries({ queryKey: SESSION_QUERY_KEY })
    await navigate({ href: returnTo, replace: true })
  })

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
      {errors.root !== undefined && (
        <Alert variant="destructive">
          <AlertDescription>{errors.root.message}</AlertDescription>
        </Alert>
      )}

      <FloatingField
        label={t('auth:signIn.username')}
        autoComplete="username"
        autoFocus
        tone={errors.username === undefined ? 'normal' : 'error'}
        hint={
          errors.username === undefined
            ? undefined
            : t(errors.username.message ?? 'common:validation.required')
        }
        {...register('username')}
      />

      <FloatingField
        label={t('common:auth.password')}
        type="password"
        autoComplete="current-password"
        tone={errors.password === undefined ? 'normal' : 'error'}
        hint={
          errors.password === undefined
            ? undefined
            : t(errors.password.message ?? 'common:validation.required')
        }
        {...register('password')}
      />

      {/* The screen's single primary action. */}
      <Button type="submit" variant="default" isLoading={isSubmitting}>
        {t('common:actions.signIn')}
      </Button>
    </form>
  )
}
